import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, History, LockKeyhole, PencilLine, Save, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PERMANENT_ARCHES = {
  upper: ["18", "17", "16", "15", "14", "13", "12", "11", "21", "22", "23", "24", "25", "26", "27", "28"],
  lower: ["48", "47", "46", "45", "44", "43", "42", "41", "31", "32", "33", "34", "35", "36", "37", "38"],
};

const PRIMARY_ARCHES = {
  upper: ["55", "54", "53", "52", "51", "61", "62", "63", "64", "65"],
  lower: ["85", "84", "83", "82", "81", "71", "72", "73", "74", "75"],
};

const WHOLE_TOOTH_FINDINGS = new Set([
  "missing", "unerupted", "impacted", "retained_primary", "supernumerary",
  "extraction", "implant", "crown", "root_canal", "bridge_abutment", "bridge_pontic",
]);

const UNIVERSAL_PERMANENT = {
  "18": "1", "17": "2", "16": "3", "15": "4", "14": "5", "13": "6", "12": "7", "11": "8",
  "21": "9", "22": "10", "23": "11", "24": "12", "25": "13", "26": "14", "27": "15", "28": "16",
  "38": "17", "37": "18", "36": "19", "35": "20", "34": "21", "33": "22", "32": "23", "31": "24",
  "41": "25", "42": "26", "43": "27", "44": "28", "45": "29", "46": "30", "47": "31", "48": "32",
};

const UNIVERSAL_PRIMARY = {
  "55": "A", "54": "B", "53": "C", "52": "D", "51": "E", "61": "F", "62": "G", "63": "H", "64": "I", "65": "J",
  "75": "K", "74": "L", "73": "M", "72": "N", "71": "O", "81": "P", "82": "Q", "83": "R", "84": "S", "85": "T",
};

export const DENTAL_STATUS = {
  condition: { label: "Condition", color: "#dc2626", className: "bg-red-100 text-red-800 border-red-300", pattern: "●" },
  existing: { label: "Existing", color: "#2563eb", className: "bg-blue-100 text-blue-800 border-blue-300", pattern: "■" },
  planned: { label: "Planned", color: "#7c3aed", className: "bg-violet-100 text-violet-800 border-violet-300", pattern: "◆" },
  accepted: { label: "Accepted", color: "#0891b2", className: "bg-cyan-100 text-cyan-800 border-cyan-300", pattern: "✓" },
  in_progress: { label: "In progress", color: "#d97706", className: "bg-amber-100 text-amber-800 border-amber-300", pattern: "◐" },
  completed: { label: "Completed", color: "#059669", className: "bg-emerald-100 text-emerald-800 border-emerald-300", pattern: "✓" },
  referred: { label: "Referred", color: "#db2777", className: "bg-pink-100 text-pink-800 border-pink-300", pattern: "↗" },
  declined: { label: "Declined", color: "#475569", className: "bg-slate-100 text-slate-700 border-slate-300", pattern: "×" },
  monitor: { label: "Monitor", color: "#9333ea", className: "bg-purple-100 text-purple-800 border-purple-300", pattern: "◉" },
};

export const DENTAL_CONCEPTS = [
  ["healthy", "Healthy"], ["caries", "Caries"], ["fracture", "Fracture or crack"], ["erosion", "Erosion"],
  ["sensitivity", "Sensitivity"], ["periapical_concern", "Periapical concern"], ["missing", "Missing tooth"],
  ["unerupted", "Unerupted"], ["impacted", "Impacted"], ["retained_primary", "Retained primary tooth"],
  ["supernumerary", "Supernumerary tooth"], ["filling", "Filling"],
  ["crown", "Crown"], ["veneer", "Veneer"], ["sealant", "Sealant"],
  ["root_canal", "Root canal treatment"], ["implant", "Implant"], ["bridge_abutment", "Bridge abutment"],
  ["bridge_pontic", "Bridge pontic"], ["extraction", "Extraction"],
];

function isPosterior(toothId) {
  const position = Number(String(toothId).slice(-1));
  return position >= 4;
}

export function validSurfacesForTooth(toothId) {
  const quadrant = Number(String(toothId)[0]);
  const posterior = isPosterior(toothId);
  return ["mesial", "distal", posterior ? "buccal" : "labial", [1, 2, 5, 6].includes(quadrant) ? "palatal" : "lingual", posterior ? "occlusal" : "incisal"];
}

export function proximalSurfaceLayout(toothId) {
  const patientRight = ["1", "4", "5", "8"].includes(String(toothId)[0]);
  return patientRight ? { left: "distal", right: "mesial" } : { left: "mesial", right: "distal" };
}

export function toothFamily(toothId) {
  const quadrant = Number(String(toothId)[0]);
  const position = Number(String(toothId)[1]);
  if (position <= 2) return "Incisor";
  if (position === 3) return "Canine";
  if (quadrant >= 5) return "Molar";
  if (position <= 5) return "Premolar";
  return "Molar";
}

export function displayToothNumber(toothId, system = "fdi") {
  if (system === "universal") return UNIVERSAL_PERMANENT[toothId] || UNIVERSAL_PRIMARY[toothId] || toothId;
  if (system === "palmer") {
    const quadrant = String(toothId)[0];
    const position = String(toothId)[1];
    const marks = { "1": "UR", "2": "UL", "3": "LL", "4": "LR", "5": "UR", "6": "UL", "7": "LL", "8": "LR" };
    return `${marks[quadrant] || ""}${position}`;
  }
  return toothId;
}

export function suggestDentition(dateOfBirth, referenceDate = new Date()) {
  if (!dateOfBirth) return "permanent";
  const born = new Date(dateOfBirth);
  if (Number.isNaN(born.getTime())) return "permanent";
  let age = referenceDate.getFullYear() - born.getFullYear();
  const beforeBirthday = referenceDate.getMonth() < born.getMonth() || (referenceDate.getMonth() === born.getMonth() && referenceDate.getDate() < born.getDate());
  if (beforeBirthday) age -= 1;
  if (age < 6) return "primary";
  if (age < 13) return "mixed";
  return "permanent";
}

export function clinicalChartVisibility(mode, canViewDental = false) {
  const resolved = ["general", "dental", "both"].includes(mode) ? mode : "general";
  return { body: resolved !== "dental", dental: canViewDental && resolved !== "general" };
}

function ToothGlyph({ toothId, numberingSystem, selected, selectedSurfaces, status, disabled, onWhole, onSurface }) {
  const surfaces = validSurfacesForTooth(toothId);
  const posterior = isPosterior(toothId);
  const outerSurface = posterior ? "buccal" : "labial";
  const innerSurface = surfaces.includes("palatal") ? "palatal" : "lingual";
  const proximal = proximalSurfaceLayout(toothId);
  const statusColor = status ? DENTAL_STATUS[status]?.color : null;
  const fillFor = (surface) => selectedSurfaces.includes(surface) ? "#14b8a6" : statusColor || "#ffffff";
  const surfaceProps = (surface, label) => ({
    role: "button", tabIndex: disabled ? -1 : 0, "aria-label": `${label} surface of tooth ${displayToothNumber(toothId, numberingSystem)}`,
    "aria-pressed": selectedSurfaces.includes(surface),
    fill: fillFor(surface), stroke: "#64748b", strokeWidth: 1.2,
    onClick: (event) => { event.stopPropagation(); if (!disabled) onSurface(toothId, surface); },
    onKeyDown: (event) => { if (!disabled && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onSurface(toothId, surface); } },
    className: "focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-teal-600",
    style: { cursor: disabled ? "default" : "pointer" },
  });
  return <div className={`rounded-xl p-1 text-center transition ${selected ? "bg-teal-50 ring-2 ring-teal-500" : "hover:bg-slate-50"}`} data-testid={`tooth-${toothId}`}>
    <svg viewBox="0 0 64 94" className="mx-auto h-20 w-14" aria-label={`${toothFamily(toothId)} tooth ${displayToothNumber(toothId, numberingSystem)}`}>
      <path d={toothFamily(toothId) === "Incisor" ? "M25 5 C18 15 18 31 22 40 L25 83 C26 92 30 92 32 69 C34 92 38 92 39 83 L42 40 C46 31 46 15 39 5 C35 2 29 2 25 5Z" : toothFamily(toothId) === "Canine" ? "M24 8 L32 1 L40 8 C46 18 46 31 42 40 L38 85 C37 94 33 94 32 70 C31 94 27 94 26 85 L22 40 C18 31 18 18 24 8Z" : toothFamily(toothId) === "Premolar" ? "M20 7 C13 15 14 31 18 40 L23 82 C24 92 29 92 32 72 C35 92 40 92 41 82 L46 40 C50 31 51 15 44 7 C37 2 27 2 20 7Z" : "M16 7 C9 16 11 31 16 40 L21 81 C22 91 28 92 32 71 C36 92 42 91 43 81 L48 40 C53 31 55 16 48 7 C39 1 25 1 16 7Z"} fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
      <g onClick={() => !disabled && onWhole(toothId)} onKeyDown={(event) => { if (!disabled && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); onWhole(toothId); } }} role="button" tabIndex={disabled ? -1 : 0} aria-pressed={selected && selectedSurfaces.length === 0} aria-label={`Select whole tooth ${displayToothNumber(toothId, numberingSystem)}`} className="focus:outline focus:outline-2 focus:outline-teal-600" style={{ cursor: disabled ? "default" : "pointer" }}>
        <path d="M14 12 Q32 1 50 12 L47 36 Q32 44 17 36Z" fill={selected && selectedSurfaces.length === 0 ? "#99f6e4" : statusColor || "#fff"} stroke="#475569" strokeWidth="1.5" />
      </g>
      <path d="M18 14 L28 24 L18 34Z" data-visual-side="left" {...surfaceProps(proximal.left, proximal.left === "mesial" ? "Mesial" : "Distal")} />
      <path d="M46 14 L36 24 L46 34Z" data-visual-side="right" {...surfaceProps(proximal.right, proximal.right === "mesial" ? "Mesial" : "Distal")} />
      <path d="M20 12 L44 12 L36 21 L28 21Z" {...surfaceProps(outerSurface, outerSurface === "labial" ? "Labial" : "Buccal")} />
      <path d="M20 36 L44 36 L36 27 L28 27Z" {...surfaceProps(innerSurface, innerSurface === "palatal" ? "Palatal" : "Lingual")} />
      {posterior
        ? <rect x="27" y="20" width="10" height="9" rx="2" {...surfaceProps("occlusal", "Occlusal")} />
        : <path d="M27 20 L37 20 L35 29 L29 29Z" {...surfaceProps("incisal", "Incisal")} />}
    </svg>
    <button type="button" disabled={disabled} onClick={() => onWhole(toothId)} className="min-h-7 w-full rounded text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500" aria-pressed={selected && selectedSurfaces.length === 0} aria-label={`Select tooth ${displayToothNumber(toothId, numberingSystem)}`}>
      {displayToothNumber(toothId, numberingSystem)} {status ? DENTAL_STATUS[status]?.pattern : ""}
      <span className="block text-[8px] font-medium text-slate-400">{toothFamily(toothId)}</span>
    </button>
  </div>;
}

function statusForTooth(entries, toothId) {
  const entry = [...entries].reverse().find((item) => item.tooth_code === toothId);
  return entry?.status;
}

function OdontogramArch({ label, teeth, selection, ...props }) {
  return <section aria-label={`${label} teeth`}>
    <div className="mb-2 flex items-center gap-3"><div className="h-px flex-1 bg-slate-200" /><h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</h4><div className="h-px flex-1 bg-slate-200" /></div>
    <div className="grid grid-cols-5 gap-1 sm:grid-cols-10 xl:grid-cols-16">
      {teeth.map((toothId) => <ToothGlyph key={toothId} toothId={toothId} {...props} selected={Object.prototype.hasOwnProperty.call(selection, toothId)} selectedSurfaces={selection[toothId] || []} status={statusForTooth(props.entries, toothId)} />)}
    </div>
  </section>;
}


export function archToothPlacement(index, count, arch = "upper") {
  const ratio = count <= 1 ? 0 : (index / (count - 1)) * 2 - 1;
  const depth = Math.pow(Math.abs(ratio), 1.65);
  return {
    x: 260 + ratio * 205,
    y: arch === "upper" ? 58 + depth * 118 : 212 - depth * 118,
    rotation: arch === "upper" ? ratio * 58 : -ratio * 58,
  };
}

function archCrownMetrics(toothId) {
  switch (toothFamily(toothId)) {
    case "Incisor": return { width: 24, height: 34, radius: 7 };
    case "Canine": return { width: 25, height: 36, radius: 10 };
    case "Premolar": return { width: 29, height: 34, radius: 12 };
    default: return { width: 34, height: 36, radius: 11 };
  }
}

function ArchTooth({ toothId, arch, index, count, numberingSystem, selected, selectedSurfaces, status, disabled, onWhole, onSurface }) {
  const placement = archToothPlacement(index, count, arch);
  const { width, height, radius } = archCrownMetrics(toothId);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const surfaces = validSurfacesForTooth(toothId);
  const posterior = isPosterior(toothId);
  const outerSurface = posterior ? "buccal" : "labial";
  const innerSurface = surfaces.includes("palatal") ? "palatal" : "lingual";
  const bitingSurface = posterior ? "occlusal" : "incisal";
  const proximal = proximalSurfaceLayout(toothId);
  const outerY = arch === "upper" ? -halfHeight : halfHeight;
  const innerY = -outerY;
  const outerInnerEdgeY = arch === "upper" ? -halfHeight * 0.22 : halfHeight * 0.22;
  const innerInnerEdgeY = -outerInnerEdgeY;
  const meta = status ? DENTAL_STATUS[status] : null;
  const number = displayToothNumber(toothId, numberingSystem);
  const wholeSelected = selected && selectedSurfaces.length === 0;
  const activate = (handler) => (event) => {
    if (event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
    if (event.type === "keydown") event.preventDefault();
    event.stopPropagation();
    if (!disabled) handler();
  };
  const surfaceProps = (surface, label) => ({
    role: "button", tabIndex: disabled ? -1 : 0,
    "aria-label": `${label} surface of tooth ${number}`,
    "aria-pressed": selectedSurfaces.includes(surface), "aria-disabled": disabled || undefined,
    fill: selectedSurfaces.includes(surface) ? "#14b8a6" : "#fffaf0",
    stroke: "#94a3b8", strokeWidth: 0.75,
    onClick: activate(() => onSurface(toothId, surface)),
    onKeyDown: activate(() => onSurface(toothId, surface)),
    className: "focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-teal-600",
    style: { cursor: disabled ? "default" : "pointer" },
  });
  const statusLabel = meta ? `, ${meta.label}` : "";
  return <g transform={`translate(${placement.x} ${placement.y}) rotate(${placement.rotation})`} data-testid={`arch-tooth-${toothId}-${arch}`} aria-label={`${toothFamily(toothId)} tooth ${number}${statusLabel}`}>
    <rect x={-halfWidth - 3} y={-halfHeight - 3} width={width + 6} height={height + 6} rx={radius + 2} fill={wholeSelected ? "#ccfbf1" : meta?.color || "#fdf4e7"} fillOpacity={wholeSelected ? 1 : meta ? 0.24 : 1} stroke={wholeSelected ? "#0f766e" : "#d6b98c"} strokeWidth={wholeSelected ? 2.5 : 1.4} role="button" tabIndex={disabled ? -1 : 0} aria-label={`Select whole tooth ${number}`} aria-pressed={wholeSelected} aria-disabled={disabled || undefined} onClick={activate(() => onWhole(toothId))} onKeyDown={activate(() => onWhole(toothId))} className="focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-teal-600" style={{ cursor: disabled ? "default" : "pointer" }} />
    <path d={`M ${-halfWidth} ${-halfHeight} L 0 ${-halfHeight * 0.28} L ${-halfWidth} ${halfHeight} Q ${-halfWidth - 2} 0 ${-halfWidth} ${-halfHeight}Z`} data-visual-side="left" {...surfaceProps(proximal.left, proximal.left === "mesial" ? "Mesial" : "Distal")} />
    <path d={`M ${halfWidth} ${-halfHeight} L 0 ${-halfHeight * 0.28} L ${halfWidth} ${halfHeight} Q ${halfWidth + 2} 0 ${halfWidth} ${-halfHeight}Z`} data-visual-side="right" {...surfaceProps(proximal.right, proximal.right === "mesial" ? "Mesial" : "Distal")} />
    <path d={`M ${-halfWidth + 1} ${outerY} Q 0 ${outerY + (arch === "upper" ? -3 : 3)} ${halfWidth - 1} ${outerY} L ${halfWidth * 0.42} ${outerInnerEdgeY} L ${-halfWidth * 0.42} ${outerInnerEdgeY}Z`} {...surfaceProps(outerSurface, outerSurface === "labial" ? "Labial" : "Buccal")} />
    <path d={`M ${-halfWidth + 1} ${innerY} Q 0 ${innerY + (arch === "upper" ? 3 : -3)} ${halfWidth - 1} ${innerY} L ${halfWidth * 0.42} ${innerInnerEdgeY} L ${-halfWidth * 0.42} ${innerInnerEdgeY}Z`} {...surfaceProps(innerSurface, innerSurface === "palatal" ? "Palatal" : "Lingual")} />
    {posterior
      ? <ellipse cx="0" cy="0" rx={Math.max(6, width * 0.22)} ry={Math.max(5, height * 0.2)} {...surfaceProps(bitingSurface, "Occlusal")} />
      : <rect x={-Math.max(5, width * 0.2)} y={-4} width={Math.max(10, width * 0.4)} height="8" rx="2" {...surfaceProps(bitingSurface, "Incisal")} />}
    <g transform={`translate(0 ${halfHeight + 13})`} role="button" tabIndex={disabled ? -1 : 0} aria-label={`Select tooth ${number} in ${arch} jaw`} aria-pressed={wholeSelected} aria-disabled={disabled || undefined} onClick={activate(() => onWhole(toothId))} onKeyDown={activate(() => onWhole(toothId))} className="focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-teal-600" style={{ cursor: disabled ? "default" : "pointer" }}>
      <rect x="-14" y="-8" width="28" height="16" rx="8" fill="#fff" stroke={wholeSelected ? "#0f766e" : "#cbd5e1"} />
      <text textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#334155">{number}</text>
    </g>
    {meta && <g transform={`translate(${halfWidth + 7} ${-halfHeight - 7})`} aria-label={`${meta.label} status`}>
      <circle r="8" fill={meta.color} stroke="#fff" strokeWidth="2" />
      <text textAnchor="middle" dominantBaseline="central" fontSize="8" fontWeight="800" fill="#fff">{meta.pattern}</text>
    </g>}
  </g>;
}

function JawArchView({ label, arch, teeth, selection, ...props }) {
  const gumPath = arch === "upper"
    ? "M34 207 C30 96 119 27 260 25 C401 27 490 96 486 207 C454 171 424 145 389 126 C351 105 308 94 260 94 C212 94 169 105 131 126 C96 145 66 171 34 207Z"
    : "M34 63 C30 174 119 243 260 245 C401 243 490 174 486 63 C454 99 424 125 389 144 C351 165 308 176 260 176 C212 176 169 165 131 144 C96 125 66 99 34 63Z";
  const innerPath = arch === "upper"
    ? "M126 190 C123 119 181 72 260 70 C339 72 397 119 394 190 C357 155 314 138 260 138 C206 138 163 155 126 190Z"
    : "M126 80 C123 151 181 198 260 200 C339 198 397 151 394 80 C357 115 314 132 260 132 C206 132 163 115 126 80Z";
  return <section className="min-w-0 rounded-2xl border border-rose-100 bg-gradient-to-b from-rose-50 to-white p-3" aria-label={`${label} jaw arch`} data-testid={`arch-${arch}-${props.dentitionLabel.toLowerCase()}`}>
    <div className="mb-1 flex items-center justify-between gap-2"><h5 className="text-sm font-bold text-mg-navy">{label} jaw</h5><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Patient view</span></div>
    <div className="overflow-x-auto">
      <svg viewBox="0 0 520 270" className="mx-auto block h-auto min-w-[320px] max-w-[620px] w-full" role="group" aria-label={`${label} jaw ${props.dentitionLabel.toLowerCase()} dentition diagram`}>
        <defs><linearGradient id={`gum-${arch}-${props.dentitionLabel}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fecdd3" /><stop offset="1" stopColor="#fda4af" /></linearGradient></defs>
        <path d={gumPath} fill={`url(#gum-${arch}-${props.dentitionLabel})`} stroke="#e11d48" strokeOpacity="0.35" strokeWidth="2" />
        <path d={innerPath} fill={arch === "upper" ? "#f8b4bd" : "#fbc4cc"} stroke="#e8798b" strokeOpacity="0.45" />
        <text x="260" y={arch === "upper" ? "120" : "154"} textAnchor="middle" fontSize="13" fontWeight="700" fill="#9f1239" opacity="0.72">{label.toUpperCase()} ARCH</text>
        {teeth.map((toothId, index) => <ArchTooth key={toothId} toothId={toothId} arch={arch} index={index} count={teeth.length} {...props} selected={Object.prototype.hasOwnProperty.call(selection, toothId)} selectedSurfaces={selection[toothId] || []} status={statusForTooth(props.entries, toothId)} />)}
      </svg>
    </div>
  </section>;
}

function formatDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function EntryList({ title, entries, signed = false, onRemove }) {
  return <section className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="mb-3 flex items-center gap-2"><h4 className="font-bold text-mg-navy">{title}</h4>{signed && <LockKeyhole className="h-4 w-4 text-slate-400" aria-label="Signed and read only" />}</div>
    {!entries.length && <p className="text-sm text-slate-500">No dental chart entries.</p>}
    <div className="space-y-2">{entries.map((entry, index) => {
      const meta = DENTAL_STATUS[entry.status] || DENTAL_STATUS.condition;
      const concept = DENTAL_CONCEPTS.find(([key]) => key === entry.finding_code)?.[1] || entry.finding_label || entry.finding_code || "Dental entry";
      return <article key={entry.entry_id || index} className="rounded-lg border border-slate-200 p-3 text-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div><strong>{concept}</strong><div className="mt-1 text-xs text-slate-600">Tooth {entry.tooth_code || "—"} · {(entry.surfaces || []).join(", ") || "whole tooth"}</div></div>
          <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${meta.className}`}>{meta.pattern} {meta.label}</span>
        </div>
        {entry.notes && <p className="mt-2 text-xs text-slate-600">{entry.notes}</p>}
        {(entry.recorded_by_name || entry.recorded_at) && <p className="mt-2 text-[11px] text-slate-400">{entry.recorded_by_name || "Clinic user"} · {formatDate(entry.recorded_at)}</p>}
        {onRemove && <button type="button" onClick={() => onRemove(index)} className="mt-2 text-xs font-semibold text-red-600">Remove draft entry</button>}
      </article>;
    })}</div>
  </section>;
}

export function DentalChart({ encounterId, patientDateOfBirth, draftScope = "clinic-user", canEdit = false, canSign = false, canAmend = false, readOnly = false, encounterClosed = false, initialConfig = {}, amendRequestToken = 0, onDirtyChange = () => {} }) {
  const initialDentition = initialConfig.default_dentition === "age_assisted" ? suggestDentition(patientDateOfBirth) : initialConfig.default_dentition;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(initialConfig);
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState([]);
  const [history, setHistory] = useState([]);
  const [dentition, setDentition] = useState(["primary", "mixed"].includes(initialDentition) ? initialDentition : "permanent");
  const [selected, setSelected] = useState({});
  const [viewMode, setViewMode] = useState("surface");
  const [editor, setEditor] = useState({ concept: "caries", status: "condition", severity: "", material: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [amending, setAmending] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [conflictingDraft, setConflictingDraft] = useState(null);
  const [reviewConflictingDraft, setReviewConflictingDraft] = useState(false);
  const handledAmendRequestRef = useRef(0);
  const draftStorageKey = `medigenie:dental-chart-draft:${draftScope}:${encounterId}`;
  const chartContextRef = useRef({ initialConfig, patientDateOfBirth });
  chartContextRef.current = { initialConfig, patientDateOfBirth };

  const numberingSystem = config.dental_numbering_system || initialConfig.dental_numbering_system || "fdi";
  const isEditableAmendment = canAmend && session?.status === "editable" && Boolean(session?.parent_session_id);
  const immutable = readOnly || !canEdit || (encounterClosed && !isEditableAmendment) || session?.status === "signed" || Boolean(session?.signed_at);
  const chartArchSets = dentition === "mixed"
    ? [{ name: "Permanent", arches: PERMANENT_ARCHES }, { name: "Primary", arches: PRIMARY_ARCHES }]
    : [{ name: "", arches: dentition === "primary" ? PRIMARY_ARCHES : PERMANENT_ARCHES }];
  const toothIds = Object.keys(selected);

  useEffect(() => {
    let cancelled = false;
    const { initialConfig, patientDateOfBirth } = chartContextRef.current;
    setLoading(true);
    api.get(`/encounters/${encounterId}/dental-chart`).then(({ data }) => {
      if (cancelled) return;
      setConfig({ ...initialConfig, ...(data.config || {}) });
      setSession(data.session || null);
      const serverEntries = data.entries || data.session?.entries || [];
      setEntries(serverEntries);
      setHistory(data.history || []);
      const configuredDentition = data.config?.default_dentition || initialConfig.default_dentition || "permanent";
      setDentition(data.session?.dentition || (configuredDentition === "age_assisted" ? suggestDentition(patientDateOfBirth) : configuredDentition));
      try {
        const recovered = JSON.parse(window.sessionStorage.getItem(draftStorageKey) || "null");
        const sameSession = (recovered?.session_id || null) === (data.session?.session_id || null);
        const sameRevision = (recovered?.revision ?? null) === (data.session?.revision ?? null);
        if (recovered && sameSession && sameRevision && data.session?.status !== "signed") {
          setEntries(recovered.entries || serverEntries);
          setDentition(recovered.dentition || data.session?.dentition || "permanent");
          setDirty(true);
          toast.info("Unsaved dental chart changes were restored in this browser.");
        } else if (recovered) {
          setConflictingDraft(recovered);
          setReviewConflictingDraft(false);
        }
      } catch { window.sessionStorage.removeItem(draftStorageKey); }
      setError("");
    }).catch((requestError) => {
      if (!cancelled) setError(requestError?.response?.data?.detail || "The dental chart could not be loaded.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [encounterId, draftStorageKey]); // Reload only when encounter or draft owner changes.

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!amendRequestToken || loading || handledAmendRequestRef.current === amendRequestToken) return;
    handledAmendRequestRef.current = amendRequestToken;
    if (session?.status === "signed" && canAmend) {
      setError("");
      setAmending(true);
      return;
    }
    if (session?.status === "editable" && session?.parent_session_id && canAmend) {
      setError("");
      return;
    }
    setError("A signed dental chart is required before an amendment can be created.");
  }, [amendRequestToken, canAmend, loading, session?.parent_session_id, session?.status]);

  useEffect(() => {
    if (!dirty) return;
    window.sessionStorage.setItem(draftStorageKey, JSON.stringify({ session_id: session?.session_id || null, revision: session?.revision ?? null, dentition, entries }));
  }, [dirty, draftStorageKey, dentition, entries, session?.revision, session?.session_id]);

  const toggleWhole = (toothId) => {
    if (immutable) return;
    setSelected((current) => {
      const next = { ...current };
      if (next[toothId] && next[toothId].length === 0) delete next[toothId];
      else next[toothId] = [];
      return next;
    });
  };

  const toggleSurface = (toothId, surface) => {
    if (immutable) return;
    setSelected((current) => {
      const surfaces = current[toothId] || [];
      const nextSurfaces = surfaces.includes(surface) ? surfaces.filter((item) => item !== surface) : [...surfaces, surface];
      const next = { ...current };
      if (!nextSurfaces.length && surfaces.includes(surface)) delete next[toothId];
      else next[toothId] = nextSurfaces;
      return next;
    });
  };

  const addEntry = () => {
    if (!toothIds.length) return toast.error("Select at least one tooth or tooth surface.");
    const newEntries = toothIds.map((toothId) => ({
      entry_id: globalThis.crypto?.randomUUID?.() || `${Date.now()}-${toothId}`,
      tooth_code: toothId, surfaces: WHOLE_TOOTH_FINDINGS.has(editor.concept) ? ["whole"] : (selected[toothId]?.length ? selected[toothId] : ["whole"]), finding_code: editor.concept,
      status: editor.status,
      notes: [editor.severity && `Severity: ${editor.severity}`, editor.material && `Material: ${editor.material}`, editor.notes.trim()].filter(Boolean).join(" · ") || null,
    }));
    setEntries((current) => [...current, ...newEntries]);
    setSelected({});
    setEditor((current) => ({ ...current, severity: "", material: "", notes: "" }));
    setDirty(true);
  };

  const ensureSession = async () => {
    if (session?.session_id) return session;
    const { data } = await api.post(`/encounters/${encounterId}/dental-chart-sessions`, { dentition });
    const created = data.session || data;
    setSession(created);
    if (data.entries) setEntries(data.entries);
    return created;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const active = await ensureSession();
      const { data } = await api.put(`/dental-chart-sessions/${active.session_id}/entries`, { revision: active.revision ?? 0, entries });
      setSession(data.session || { ...active, revision: data.revision ?? active.revision });
      setEntries(data.entries || entries);
      setDirty(false);
      window.sessionStorage.removeItem(draftStorageKey);
      toast.success("Dental chart draft saved");
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        const preserved = { session_id: session?.session_id || null, revision: session?.revision ?? null, dentition, entries };
        window.sessionStorage.setItem(draftStorageKey, JSON.stringify(preserved));
        setConflictingDraft(preserved);
        setError("Server chart changed. Your unsaved copy is preserved in this browser. Copy or review it before choosing discard and use server.");
      }
      else setError(requestError?.response?.data?.detail || "The dental chart draft could not be saved.");
    } finally { setSaving(false); }
  };

  const signChart = async () => {
    setSaving(true);
    try {
      const hadSession = Boolean(session?.session_id);
      let active = await ensureSession();
      if (dirty || !hadSession) {
        const saved = await api.put(`/dental-chart-sessions/${active.session_id}/entries`, { revision: active.revision ?? 0, entries });
        active = saved.data.session || { ...active, revision: saved.data.revision ?? active.revision };
      }
      const { data } = await api.post(`/dental-chart-sessions/${active.session_id}/sign`, { revision: active.revision ?? 0 });
      setSession(data.session || data);
      setEntries(data.entries || entries);
      setDirty(false);
      window.sessionStorage.removeItem(draftStorageKey);
      toast.success("Dental chart signed");
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        const preserved = { session_id: session?.session_id || null, revision: session?.revision ?? null, dentition, entries };
        window.sessionStorage.setItem(draftStorageKey, JSON.stringify(preserved));
        setConflictingDraft(preserved);
        setError("Server chart changed. Your unsaved copy is preserved in this browser. Copy or review it before choosing discard and use server.");
      }
      else setError(requestError?.response?.data?.detail || "The dental chart could not be signed.");
    } finally { setSaving(false); }
  };

  const amendSignedChart = async () => {
    if (!amendReason.trim() || !session?.session_id) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/dental-chart-sessions/${session.session_id}/amendments`, { reason: amendReason.trim() });
      setSession(data.session);
      setEntries(data.entries || []);
      setAmending(false);
      setAmendReason("");
      setDirty(false);
      toast.success("A new amendable dental chart was created. The signed chart remains unchanged.");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "The signed dental chart could not be amended.");
    } finally { setSaving(false); }
  };

  const copyPreservedDraft = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(conflictingDraft, null, 2));
      toast.success("Preserved dental chart copied");
    } catch { toast.error("The preserved chart could not be copied. Use Review preserved copy instead."); }
  };

  const discardPreservedDraft = () => {
    window.sessionStorage.removeItem(draftStorageKey);
    setConflictingDraft(null);
    setReviewConflictingDraft(false);
    setError("");
    toast.success("Preserved copy discarded; the server chart remains open");
  };

  const chartEntries = useMemo(() => [...entries], [entries]);
  if (loading) return <div className="mg-card p-6 text-sm text-slate-500" role="status">Loading dental chart…</div>;

  const signedHistory = history.filter((item) => item.event === "signed" || item.status === "signed" || item.signed_at);
  return <div className="space-y-5" data-testid="dental-chart">
    <header className="mg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><h3 className="text-lg font-bold text-mg-navy">Dental chart</h3><p className="mt-1 text-sm text-slate-500">Select a tooth or its surface, then record a structured finding or treatment.</p></div>
        <div className="flex flex-wrap gap-2">
          <label className="text-xs font-semibold text-slate-600">Dentition<select className="mg-input mt-1 block" value={dentition} disabled={immutable || Boolean(session?.session_id)} onChange={(event) => setDentition(event.target.value)} aria-label="Dentition"><option value="permanent">Permanent</option><option value="primary">Primary</option><option value="mixed">Mixed</option></select>{config.default_dentition === "age_assisted" && !session?.session_id && <span className="mt-1 block max-w-48 font-normal text-slate-500">Suggested from age. Confirm before saving.</span>}</label>
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"><strong>Numbering</strong><br />{numberingSystem.toUpperCase()}</div>
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="group" aria-label="Dental chart view">
            <button type="button" aria-pressed={viewMode === "surface"} onClick={() => setViewMode("surface")} className={`rounded-md px-3 py-2 text-xs font-semibold ${viewMode === "surface" ? "bg-white text-mg-navy shadow-sm" : "text-slate-500"}`}>Surface grid</button>
            <button type="button" aria-pressed={viewMode === "arch"} onClick={() => setViewMode("arch")} className={`rounded-md px-3 py-2 text-xs font-semibold ${viewMode === "arch" ? "bg-white text-mg-navy shadow-sm" : "text-slate-500"}`}>Jaw arches</button>
          </div>
          {session?.status === "signed" && <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700"><ShieldCheck className="h-4 w-4" /> Signed</div>}
          {session?.status === "signed" && canAmend && <button type="button" className="mg-btn-secondary text-xs" onClick={() => setAmending(true)}><PencilLine className="h-4 w-4" /> Amend signed chart</button>}
        </div>
      </div>
      {error && <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {conflictingDraft && <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="dental-draft-conflict"><p className="text-sm font-semibold text-amber-950">Server chart changed. Your unsaved copy is preserved in this browser.</p><p className="mt-1 text-xs text-amber-800">Copy or review it before choosing discard and use server.</p><div className="mt-3 flex flex-wrap gap-2"><button type="button" className="mg-btn-secondary text-xs" onClick={() => setReviewConflictingDraft((value) => !value)}>{reviewConflictingDraft ? "Hide preserved copy" : "Review preserved copy"}</button><button type="button" className="mg-btn-secondary text-xs" onClick={copyPreservedDraft}>Copy preserved copy</button><button type="button" className="text-xs font-semibold text-red-700 underline" onClick={discardPreservedDraft}>Discard preserved copy and use server</button></div>{reviewConflictingDraft && <div className="mt-4"><EntryList title="Preserved unsaved copy" entries={conflictingDraft.entries || []} signed /></div>}</div>}
      {amending && <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"><label className="text-xs font-bold text-amber-900">Reason for amendment<textarea className="mg-input mt-1 w-full bg-white" rows={2} value={amendReason} onChange={(event) => setAmendReason(event.target.value)} autoFocus /></label><p className="mt-1 text-xs text-amber-800">The signed chart remains immutable. A linked editable copy will be created.</p><div className="mt-3 flex justify-end gap-2"><button type="button" className="mg-btn-secondary text-xs" onClick={() => { setAmending(false); setAmendReason(""); }}>Cancel</button><button type="button" className="mg-btn-primary text-xs" disabled={saving || !amendReason.trim()} onClick={amendSignedChart}>Create amendment</button></div></div>}
    </header>

    <section className="mg-card space-y-7 overflow-x-auto p-4" aria-label="Interactive odontogram">
      {viewMode === "surface"
        ? chartArchSets.map(({ name, arches: currentArches }) => <div key={name || dentition} className="space-y-5">
          {name && <h4 className="text-center text-sm font-bold text-mg-navy">{name} dentition</h4>}
          <OdontogramArch label="Upper arch" teeth={currentArches.upper} selection={selected} numberingSystem={numberingSystem} disabled={immutable} onWhole={toggleWhole} onSurface={toggleSurface} entries={chartEntries} />
          <div className="border-t border-dashed border-slate-200" />
          <OdontogramArch label="Lower arch" teeth={currentArches.lower} selection={selected} numberingSystem={numberingSystem} disabled={immutable} onWhole={toggleWhole} onSurface={toggleSurface} entries={chartEntries} />
        </div>)
        : chartArchSets.map(({ name, arches: currentArches }) => <div key={`arch-${name || dentition}`} className="space-y-4">
          {name && <h4 className="text-center text-sm font-bold text-mg-navy">{name} dentition</h4>}
          <div className="grid gap-4 xl:grid-cols-2">
            <JawArchView label="Upper" arch="upper" teeth={currentArches.upper} dentitionLabel={name || dentition} selection={selected} numberingSystem={numberingSystem} disabled={immutable} onWhole={toggleWhole} onSurface={toggleSurface} entries={chartEntries} />
            <JawArchView label="Lower" arch="lower" teeth={currentArches.lower} dentitionLabel={name || dentition} selection={selected} numberingSystem={numberingSystem} disabled={immutable} onWhole={toggleWhole} onSurface={toggleSurface} entries={chartEntries} />
          </div>
        </div>)}
    </section>

    {!immutable && <section className="mg-card p-5" aria-labelledby="dental-entry-heading">
      <h4 id="dental-entry-heading" className="font-bold text-mg-navy">Finding or treatment</h4>
      <p className="mt-1 text-xs text-slate-500">Selected: {toothIds.length ? toothIds.map((id) => `${displayToothNumber(id, numberingSystem)}${selected[id].length ? ` (${selected[id].join(", ")})` : " (whole tooth)"}`).join("; ") : "No teeth selected"}</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <label className="text-xs font-semibold">Finding or procedure<select className="mg-input mt-1 w-full" value={editor.concept} onChange={(event) => setEditor({ ...editor, concept: event.target.value })}>{DENTAL_CONCEPTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="text-xs font-semibold">Status<select className="mg-input mt-1 w-full" value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value })}>{Object.entries(DENTAL_STATUS).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}</select></label>
        <label className="text-xs font-semibold">Severity<input className="mg-input mt-1 w-full" value={editor.severity} onChange={(event) => setEditor({ ...editor, severity: event.target.value })} placeholder="Optional severity or grade" /></label>
        <label className="text-xs font-semibold">Material<input className="mg-input mt-1 w-full" value={editor.material} onChange={(event) => setEditor({ ...editor, material: event.target.value })} placeholder="Optional material" /></label>
        <label className="text-xs font-semibold md:col-span-2">Clinical note<textarea className="mg-input mt-1 w-full" rows={2} value={editor.notes} onChange={(event) => setEditor({ ...editor, notes: event.target.value })} placeholder="Optional chart note" /></label>
      </div>
      <div className="mt-4 flex justify-end"><button type="button" className="mg-btn-primary" onClick={addEntry} disabled={!toothIds.length}>Add to current chart</button></div>
    </section>}

    <div className="grid gap-5 xl:grid-cols-2">
      <EntryList title="Current encounter entries" entries={entries} signed={immutable && Boolean(session?.signed_at || session?.status === "signed")} onRemove={!immutable ? (index) => { setEntries((current) => current.filter((_, itemIndex) => itemIndex !== index)); setDirty(true); } : undefined} />
      <section className="rounded-xl border border-slate-200 bg-white p-4"><h4 className="mb-3 flex items-center gap-2 font-bold text-mg-navy"><History className="h-4 w-4" /> Signed history</h4>{!signedHistory.length && <p className="text-sm text-slate-500">No earlier signed dental charts.</p>}{signedHistory.map((item, index) => <div key={item.revision_id || item.session_id || index} className="mb-3 rounded-lg bg-slate-50 p-3 text-sm"><div className="font-semibold">Dental chart revision {item.revision || ""} · {formatDate(item.signed_at || item.created_at)}</div><div className="mt-1 text-xs text-slate-500">{item.signed_by_name || item.recorded_by_name || "Clinic user"} · {item.entry_count ?? (item.entries || []).length} entries</div></div>)}</section>
    </div>

    {!immutable && <footer className="sticky bottom-3 flex flex-wrap justify-end gap-3 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
      <button type="button" className="mg-btn-secondary" onClick={saveDraft} disabled={saving || !entries.length || !dirty}><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save draft"}</button>
      {canSign && <button type="button" className="mg-btn-primary" onClick={signChart} disabled={saving || !entries.length}><CheckCircle className="h-4 w-4" /> Sign chart</button>}
    </footer>}
  </div>;
}
